import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import FeaturedPostsCarousel from "../components/Blog/FeaturedPostsCarousel/FeaturedPostsCarousel";
import ApoieButton from "../components/ApoieButton/ApoieButton";
import PageHero from "../components/PageHero/PageHero";
import blogHeroImage from "../assets/images/blog-hero.png";
import { listarPostsPublicos } from "../services/blog";
import { getImageUrl } from "../utils/media";
import "./Blog.css";

const POSTS_PER_PAGE = 4;

function formatDate(date) {
  if (!date) return "";

  const parsedDate = new Date(date);
  if (Number.isNaN(parsedDate.getTime())) return "";

  return parsedDate.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function getAuthorName(post) {
  return post?.author?.name || post?.authorName || "7 Gerações";
}

const Blog = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    let active = true;

    async function loadPosts() {
      try {
        setLoading(true);
        setError("");

        const response = await listarPostsPublicos();
        if (!active) return;

        const orderedPosts = [...response].sort(
          (first, second) =>
            new Date(second.publishedAt || second.createdAt || 0) -
            new Date(first.publishedAt || first.createdAt || 0),
        );

        setPosts(orderedPosts);
      } catch (requestError) {
        if (!active) return;
        console.error("Erro ao carregar posts públicos:", requestError);
        setError("Não foi possível carregar os posts.");
      } finally {
        if (active) setLoading(false);
      }
    }

    loadPosts();

    return () => {
      active = false;
    };
  }, []);

  const totalPages = Math.max(1, Math.ceil(posts.length / POSTS_PER_PAGE));

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const visiblePosts = useMemo(() => {
    const start = (currentPage - 1) * POSTS_PER_PAGE;
    return posts.slice(start, start + POSTS_PER_PAGE);
  }, [currentPage, posts]);

  const openPost = (post) => {
    const identifier = post.slug || post._id || post.id;
    if (identifier) navigate("/blog/" + identifier);
  };

  return (
    <main className="blog-page">
      <PageHero
        className="page-hero--blog"
        imageUrl={blogHeroImage}
        imageShape="provided"
        title="Blog"
      />
      <ApoieButton />

      {currentPage === 1 && posts.length > 0 && (
        <FeaturedPostsCarousel posts={posts.slice(0, 3)} />
      )}

      {currentPage === 1 && (
        <div className="blog-intro">
          <h2 className="blog-intro__title">Fique por dentro!</h2>
          <p className="blog-intro__text">
            Acompanhe as novidades, projetos e histórias do Instituto 7 Gerações.
          </p>
        </div>
      )}

      <section className="blog-posts-section" aria-live="polite">
        {loading && (
          <div className="blog-posts-empty__message">Carregando posts...</div>
        )}

        {!loading && error && (
          <div className="blog-posts-empty__message">{error}</div>
        )}

        {!loading && !error && posts.length === 0 && (
          <div className="blog-posts-empty__message">
            Nenhum post publicado até o momento.
          </div>
        )}

        {!loading &&
          !error &&
          visiblePosts.map((post) => {
            const imageUrl = getImageUrl(post.images?.[0] || post.image);
            const summary = post.summary || post.content || "";

            return (
              <article
                key={post._id || post.id}
                className="blog-posts-card"
                onClick={() => openPost(post)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    openPost(post);
                  }
                }}
                role="link"
                tabIndex={0}
              >
                {imageUrl ? (
                  <img src={imageUrl} alt={"Capa do post " + post.title} />
                ) : (
                  <div
                    className="blog-posts-card__placeholder"
                    aria-hidden="true"
                  />
                )}

                <div className="blog-posts-card__content">
                  <h3 className="blog-posts__title">{post.title}</h3>
                  <p className="blog-posts__description">{summary}</p>

                  <div className="blog-posts__footer">
                    <span className="blog-posts__author">
                      Por {getAuthorName(post)}
                    </span>
                    <time
                      className="blog-posts__date"
                      dateTime={post.publishedAt || post.createdAt || ""}
                    >
                      {formatDate(post.publishedAt || post.createdAt)}
                    </time>
                  </div>
                </div>
              </article>
            );
          })}

        {!loading && !error && totalPages > 1 && (
          <nav className="blog-posts-pages" aria-label="Paginação do blog">
            {currentPage > 1 && (
              <button
                type="button"
                aria-label="Página anterior"
                onClick={() => setCurrentPage((page) => page - 1)}
              >
                &lt;
              </button>
            )}

            {Array.from({ length: totalPages }, (_, index) => {
              const page = index + 1;

              return (
                <button
                  key={page}
                  type="button"
                  className={currentPage === page ? "active" : ""}
                  aria-current={currentPage === page ? "page" : undefined}
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </button>
              );
            })}

            {currentPage < totalPages && (
              <button
                type="button"
                aria-label="Próxima página"
                onClick={() => setCurrentPage((page) => page + 1)}
              >
                &gt;
              </button>
            )}
          </nav>
        )}
      </section>
    </main>
  );
};

export default Blog;
